import {
  hasResolvedDocumentValue,
  useDocument,
} from "@shared/components/providers/SupergrainProvider"
import type { UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import UnifiedUnit from "../components/planner/card-stacks/UnifiedUnit"

interface Props {
  models: {
    cardStack: UnifiedCardStack & { attributes: { usedAs: "unit" } }
    planbook: Planbook
    rotationCalendar: RotationCalendar
    course: Course
    // unitInfo: Unit
    // units: Array<CardStackSummary>
  }
  showUnitReadonly?: boolean
}

/**
 * Adapts the Ember models passed from `Planner::CardStacks::Unit` into supergrain
 * documents, then renders the framework-agnostic UnifiedUnit.
 */
export default tracked(function UnitIsland({ models: emberModels, showUnitReadonly }: Props) {
  // The Ember models are carriers for ids; read the supergrain copies by id.
  const cardStackHandle = useDocument("card-stack", emberModels.cardStack.id)
  const planbookHandle = useDocument("planbook", emberModels.planbook.id)
  const rotationCalendarHandle = useDocument("rotation-calendar", emberModels.rotationCalendar.id)
  const courseHandle = useDocument("course", emberModels.course.id)

  if (
    cardStackHandle.status === "error" ||
    planbookHandle.status === "error" ||
    rotationCalendarHandle.status === "error" ||
    courseHandle.status === "error"
  ) {
    throw (
      cardStackHandle.error ??
      planbookHandle.error ??
      rotationCalendarHandle.error ??
      courseHandle.error
    )
  }

  if (
    !hasResolvedDocumentValue(cardStackHandle) ||
    !hasResolvedDocumentValue(planbookHandle) ||
    !hasResolvedDocumentValue(rotationCalendarHandle) ||
    !hasResolvedDocumentValue(courseHandle)
  ) {
    return null
  }

  return (
    <UnifiedUnit
      // The handle types by document type; the props already narrowed this id to a unit.
      cardStack={cardStackHandle.value as Props["models"]["cardStack"]}
      planbook={planbookHandle.value}
      rotationCalendar={rotationCalendarHandle.value}
      course={courseHandle.value}
      readonly={showUnitReadonly}
    />
  )
})
